import { Box } from '@mui/material';
import { SchoolYear } from '@schooly/api';
import { useCellStates } from '@schooly/components/data-grid';
import { Loading, VirtualizedList } from '@schooly/style';
import { FC, useCallback, useRef } from 'react';

import { NoSearchResultsFound } from '../../../components/common/NoSearchResultsFound/NoSearchResultsFound';
import { AttendanceGridShell } from './AttendanceSidebar/AttendanceGridShell';
import { AttendanceStudentStatsSummary } from './AttendanceStudentStatsSummary';
import { AttendanceTrackingPointHeaderCell } from './common/AttendanceCells';
import { AttendanceGrid } from './common/AttendanceGrid';
import { ATTENDANCE_TRACKING_POINTS_STYLING } from './common/attendanceStyling';
import { AttendanceCellState, AttendanceGridColumn, AttendanceGridStudent } from './common/types';
import {
  TrackingPointsWeekGridForView,
  useAttendanceGridForStudentTrackingPoints,
} from './data/useAttendanceGridForStudentTrackingPoints';

const renderTrackingPointHeader = (column: AttendanceGridColumn) => (
  <AttendanceTrackingPointHeaderCell column={column} />
);

const WeekTable: FC<{
  week: TrackingPointsWeekGridForView;
  getCellState: (cellId: string) => AttendanceCellState | undefined;
}> = ({ week, getCellState }) => (
  <AttendanceGrid
    rows={week.rows}
    gridColumns={week.columns}
    styling={ATTENDANCE_TRACKING_POINTS_STYLING}
    primaryHeaderLabel={week.weekLabel}
    renderDayHeader={renderTrackingPointHeader}
    getCellState={getCellState}
    withArrows={false}
  />
);

export type AttendanceTrackingPointsViewProps = {
  student: AttendanceGridStudent;
  selectedYear: SchoolYear | undefined;
  timetableStructureId?: string;
};

export const AttendanceTrackingPointsView: FC<AttendanceTrackingPointsViewProps> = ({
  student,
  selectedYear,
  timetableStructureId,
}) => {
  const {
    weekGrids,
    codes,
    counts,
    justificationCounts,
    isLoading,
    isFetchingNextPage,
    handleFetchNextPage,
  } = useAttendanceGridForStudentTrackingPoints(student, selectedYear, timetableStructureId);

  const cellContext = useCellStates<AttendanceCellState>();
  const skipInitialBottomRef = useRef(true);

  const handleAtBottomChange = useCallback(
    (atBottom: boolean) => {
      if (skipInitialBottomRef.current) {
        skipInitialBottomRef.current = false;
        return;
      }

      if (atBottom) handleFetchNextPage();
    },
    [handleFetchNextPage],
  );

  const renderContent = () => {
    if (isLoading) return <Loading />;

    if (weekGrids.length === 0) return <NoSearchResultsFound type="small" />;

    return (
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <VirtualizedList
          items={weekGrids}
          renderItem={(_, week) => (
            <Box pb={2} key={week.weekLabel}>
              <WeekTable week={week} getCellState={cellContext.getCellState} />
            </Box>
          )}
          atBottomStateChange={handleAtBottomChange}
          overscan={300}
          footer={isFetchingNextPage ? <Loading /> : null}
        />
      </Box>
    );
  };

  return (
    <AttendanceGridShell codes={codes}>
      {(isLoading || weekGrids.length > 0) && (
        <AttendanceStudentStatsSummary
          counts={counts}
          justificationCounts={justificationCounts}
          loading={isLoading}
        />
      )}

      {renderContent()}
    </AttendanceGridShell>
  );
};
